import { Toast } from '@loophealth/loop-ui-web-library';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { saveAndCheckoutModular, updateBenefitIds, validateBenefits } from '../../../../adapters';
import { ReduxState } from '../../../../redux';
import {
    fetchModularAlaCarteBenefits,
    fetchModularPlans,
    resetModularState,
    setModularCurrentStep
} from '../../../../redux/slices/ModularEnrolmentSlice';
import {
    selectedAlaCarteBenefits,
    selectModularAlaCarteBenefits,
    selectModularCurrentStep,
    selectModularFamily,
    selectModularPersonalEmail,
    selectModularPlans,
    selectModularPolicyId,
    selectModularPurchaseAttributes,
    selectModularSelectedAddOns,
    selectModularSelectedBasePlan,
    selectModularSelectedUserBenefits,
    selectModularSummary
} from '../../../../redux/slices/ModularEnrolmentSlice/selectors';
import { ModularEnrolmentSteps } from '../../../../redux/slices/ModularEnrolmentSlice/types';
import { fetchUserPolicyList } from '../../../../redux/slices/PolicyListSlice';
import { insuranceCardDateFormat, parseResponse } from '../../../../utils/common';
import { INTERNAL_ROUTES } from '../../../../utils/constants';
import { IChangePlanDeletions, IModularUpdateBenefitsPayload, IUseModularEnrolment } from '../types';
import { familyDefinition } from '../../../../utils/mapping';
import useSegment from '../../../../utils/hooks/useSegment';
import { SEGMENT_ACTIONS } from '../../../../utils/constants/SegmentActionConstants';
import useModulerEnrolmentContext from '../../../pages/SPAContainer/pages/ModularEnrolmentPage/ModulerEnrolmentContext';

const useModularEnrolment = (): IUseModularEnrolment => {
    const history = useHistory();
    const dispatch = useDispatch();
    const trackClick = useSegment('click');
    const trackPage = useSegment('page');
    const toast = Toast.useToast();
    const location = useLocation();

    const currentStep = useSelector(selectModularCurrentStep);
    const selectedBasePlan = useSelector(selectModularSelectedBasePlan);
    const selectedBenefits = useSelector(selectedAlaCarteBenefits);
    const selectedAddOns = useSelector(selectModularSelectedAddOns);
    const selectedPersonalEmail = useSelector(selectModularPersonalEmail);
    const modularData = useSelector(selectModularPlans);
    const alacarteData = useSelector(selectModularAlaCarteBenefits);
    const isSummaryLoading = useSelector(selectModularSummary).loading;
    const policyId = useSelector(selectModularPolicyId);
    const userId = useSelector((state: ReduxState) => state.user.userData.data?.userId || '');
    const familyDetails = useSelector(selectModularFamily);
    const userBenefits = useSelector(selectModularSelectedUserBenefits);
    const selectedAttributes = useSelector(selectModularPurchaseAttributes);
    const { isReeditMode, isFamilyInfoUpdated, setIsFamilyInfoUpdated, policy } = useModulerEnrolmentContext();
    const modularPlans = modularData.data || [];
    const modularLoading = modularData.loading;
    const alacartePlans = alacarteData.data || [];
    const alacarteLoading = alacarteData.loading;
    const addedMembers = familyDetails.data;
    const familyLoading = familyDetails.loading;

    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [isUpdating, setIsUpdating] = React.useState(false);
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [submitModalVisible, setSubmitModalVisible] = React.useState(false);
    const [confirmDeletions, setConfirmDeletions] = React.useState<IChangePlanDeletions | null>(null);
    const [confirmMemberModalVisible, setConfirmMemberModalVisible] = React.useState(false);

    const isProceedEnabled = useMemo(() => {
        switch (currentStep) {
        case ModularEnrolmentSteps.BASE_PLAN:
            return !!selectedBasePlan && !modularLoading;
        case ModularEnrolmentSteps.DEPENDENTS:
            return !familyLoading;
        case ModularEnrolmentSteps.HEALTHCARE_BENEFITS:
            return !alacarteLoading;
        case ModularEnrolmentSteps.SUMMARY:
            return !isSummaryLoading;
        }
    }, [selectedBasePlan, currentStep, isSummaryLoading, familyLoading, alacarteLoading, modularLoading]);

    const updateSelectedBenefits = async (params?: IModularUpdateBenefitsPayload) => {
        if (!selectedBasePlan) return;
        const deletions = confirmDeletions
            ? {
                benefitIds: confirmDeletions.benefits.map((benefit) => benefit.id),
                familyIds: confirmDeletions.family.map((family) => family.id)
            }
            : undefined;
        const purchaseAttributesKeys = Object.keys(selectedAttributes || {});
        const purchaseAttributesToSend = {};
        if (purchaseAttributesKeys.length) {
            purchaseAttributesKeys.forEach((key) => {
                purchaseAttributesToSend[key] = { userSelection: selectedAttributes?.[key] };
            });
        }
        const [err] = await parseResponse(
            updateBenefitIds(
                userId,
                policyId,
                [selectedBasePlan, ...(params?.addOns ? params.addOns : selectedAddOns), ...selectedBenefits],
                userBenefits,
                deletions,
                selectedPersonalEmail,
                purchaseAttributesToSend
            )
        );
        if (err) throw err;
    };

    const onModularPlanNext = async () => {
        if (!selectedBasePlan) return;
        const previouslySelected = modularPlans.find((plan) => plan.isSelected);

        if (previouslySelected?.id !== selectedBasePlan) {
            setIsProcessing(true);
            try {
                const { deletions } = await validateBenefits(userId, policyId, selectedBasePlan);

                if (deletions?.benefits?.length || deletions?.family?.length) {
                    const planName = modularPlans.find((p) => p.id === selectedBasePlan)?.name;
                    setConfirmDeletions({ ...deletions, planName });
                } else {
                    await handleOnConfirmUpdation();
                }
            } catch (e) {
                toast?.error((e as Error).message || 'Something went wrong');
            }
            setIsProcessing(false);
        } else dispatch(setModularCurrentStep(ModularEnrolmentSteps.DEPENDENTS));
    };

    const handleOnConfirmUpdation = async () => {
        setIsUpdating(true);
        try {
            await updateSelectedBenefits();
            setConfirmDeletions(null);
            dispatch(setModularCurrentStep(ModularEnrolmentSteps.DEPENDENTS));
            dispatch(fetchModularAlaCarteBenefits({ userId, policyId, benefitId: selectedBasePlan }));
            dispatch(fetchModularPlans({ userId, policyId })); // refetch modular after save
            if (isReeditMode) toast?.warning('Change Noted! Be sure to submit on the final page to confirm.');
        } catch (e) {
            toast?.error((e as Error).message || 'Something went wrong');
        }
        setIsUpdating(false);
    };

    const membersAdded = () => {
        const count = addedMembers?.length;
        const selectedPolicy = modularPlans?.find((policy) => policy.id === selectedBasePlan);
        const expectedCount =
            selectedPolicy?.familyDefinition && familyDefinition[selectedPolicy?.familyDefinition]?.totalDependents;
        return {
            addedMembers: count || 0,
            totalMembers: (expectedCount || 0) + 1 // count of self
        };
    };

    const onDependentsNext = () => {
        const { addedMembers, totalMembers } = membersAdded();
        if (addedMembers < totalMembers) {
            const action = SEGMENT_ACTIONS.PAGE.ADD_MORE_DEPENDENTS_MODAL;
            trackPage({
                name: action.name,
                properties: {
                    ...action.properties,
                    total_elligible_dependant: totalMembers,
                    total_dependant_added: addedMembers
                }
            });
            setConfirmMemberModalVisible(true);
        } else onConfirmMembers();
    };

    const onConfirmMembers = () => {
        const clickAction = SEGMENT_ACTIONS.CLICK.PROCEED;
        trackClick({
            name: clickAction.name,
            properties: {
                ...clickAction.properties,
                page_name: 'Add_Dependant_page',
                page_category: 'Warning_popup_modal'
            }
        });
        setConfirmMemberModalVisible(false);
        dispatch(setModularCurrentStep(ModularEnrolmentSteps.HEALTHCARE_BENEFITS));
        if (isReeditMode && isFamilyInfoUpdated) {
            setIsFamilyInfoUpdated(false);
            toast?.warning('Change Noted! Be sure to submit on the final page to confirm.');
        }
    };

    const onAlaCarteBenefitsNext = async () => {
        setIsProcessing(true);
        try {
            await updateSelectedBenefits();
            dispatch(setModularCurrentStep(ModularEnrolmentSteps.SUMMARY));
            if (isReeditMode) {
                const prevSelected: string[] = [];
                alacartePlans.forEach((p) => {
                    const ps = p.subCategories?.length ? p.subCategories : [p];
                    ps.forEach((s) => {
                        if (s.isSelected) prevSelected.push(s.id);
                    });
                });
                const isUpdated =
                    prevSelected.length !== selectedBenefits.length ||
                    prevSelected.some((p) => !selectedBenefits.includes(p));
                isUpdated && toast?.warning('Change Noted! Be sure to submit on the final page to confirm.');
            }
        } catch (e) {
            toast?.error((e as Error).message || 'Something went wrong');
        }
        setIsProcessing(false);
    };

    const handleOnSubmit = async () => {
        window.plotline('track', 'Clicked_Complete_Registration', { userId, policyId });
        setIsSubmitting(true);
        try {
            const [error] = await parseResponse(saveAndCheckoutModular(userId, policyId));
            if (error) throw error;
            // to update policy list for completion screen
            dispatch(fetchUserPolicyList({ userId }));
            history.replace(INTERNAL_ROUTES.confirm, { previousUrl: location.pathname, policyId: policyId });
            setSubmitModalVisible(false);
            dispatch(resetModularState());
        } catch (e) {
            toast?.error((e as Error).message || 'Something went wrong');
        }
        setIsSubmitting(false);
    };

    const handleBackClick = () => {
        const clickAction = SEGMENT_ACTIONS.CLICK.BACK_BUTTON;
        trackClick({
            name: clickAction.name,
            properties: {
                ...clickAction.properties,
                page_name: ModularEnrolmentSteps[currentStep],
                page_category: ModularEnrolmentSteps[currentStep]
            }
        });
        if (currentStep === ModularEnrolmentSteps.SUMMARY && alacartePlans.length === 0) {
            dispatch(setModularCurrentStep(ModularEnrolmentSteps.DEPENDENTS));
        } else dispatch(setModularCurrentStep(currentStep - 1));
    };

    const onSaveForLaterClick = () => {
        if (!policy) return;
        const dueDate = insuranceCardDateFormat(policy.enrolmentDueDate);
        history.replace(INTERNAL_ROUTES.dashboard, {
            previousUrl: INTERNAL_ROUTES.modular,
            enrolmentDueDate: dueDate
        });
    };

    return {
        isProceedEnabled,
        submitModalVisible,
        isSubmitting,
        isUpdating,
        isProcessing,
        confirmDeletions,
        confirmMemberModalVisible,
        setConfirmMemberModalVisible,
        onModularPlanNext,
        onDependentsNext,
        onAlaCarteBenefitsNext,
        setSubmitModalVisible,
        setConfirmDeletions,
        handleOnSubmit,
        handleOnConfirmUpdation,
        membersAdded,
        onConfirmMembers,
        handleBackClick,
        updateSelectedBenefits,
        onSaveForLaterClick
    };
};

export default useModularEnrolment;
